/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useContext } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { Box, Breadcrumbs } from "@mui/material";
import FileUploadButton from "../../components/upload/FileUploadButton";
import {
  fetchReservations,
  calculateValues,
  uploadReservations,
} from "../../api/reservationServices";
import { Typography } from "@mui/material";
import { TableColumns } from "../home/table_colums";
import { TableRows } from "../home/table_rows";
import Calculator from "../../components/calculator";
import camelize from "../../utils/camelize";
import { MenuHeading } from "../../components/menu_heading";
import { containerStyles } from "../reports/styles";
import { StyledButton } from "./styles";
import { IndexTable } from "../../components/index_table";
import { Loader } from "../../components/loader";
import { ActionContainer } from "../../components/action_container";
import { AbilityContext } from "../../contexts/abilityContext"
import { useSelector } from "react-redux";
import { checkAdmin } from "../../utils/checkAdmin";
import { StyledLink, StyledBreadCrumbContainer } from "../../components/navigable_breadcrumb_link/styles";

export default function Home() {
  const [data, setData] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState();
  const [fetchingDataLoader, setFetchingDataLoader] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [calculatedValues, setCalculatedValues] = useState({
    total_value: 0,
    resale_value: 0,
    total_reservation: 0,
    total_guests: 0,
    increase_sale: 0,
  });

  const [calculatorValues, setCalculatorValues] = useState({
    status: "",
    days: [],
    minGuestsPerTable: "",
    vistFromDate: null,
    vistToDate: null,
    vistFromTime: null,
    vistToTime: null,
    cancellationTime: "",
    ppa: "",
    resalePpa: "",
    shift_id: "",
  });
  const [fileUploadOpen, setFileUploadOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { ability } = useContext(AbilityContext);
  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles)

  const handleChangeField = (value, field) => {
    if (field === "shift_id") {
      setCalculatorValues({ ...calculatorValues, ["days"]: [] });
      setCalculatorValues({ ...calculatorValues, ["vistFromTime"]: null });
      setCalculatorValues({ ...calculatorValues, ["vistToTime"]: null });
    }
    setCalculatorValues({ ...calculatorValues, [field]: value });
  };

  React.useEffect(() => {
    setFetchingDataLoader(true);

    const filters = {
      status: calculatorValues?.status,
      days_of_week: calculatorValues?.days,
      minimum_guest_per_table: calculatorValues?.minGuestsPerTable,
      visit_date: {
        gteq: calculatorValues?.vistFromDate,
        lteq: calculatorValues?.vistToDate,
      },
      visit_time: {
        gteq: calculatorValues?.vistFromTime,
        lteq: calculatorValues?.vistToTime,
      },
      shift_id: calculatorValues?.shift_id,
      per_page: itemsPerPage,
      page: page,
    };
    if (calculatorValues?.status === "Canceled") {
      filters.cancellation_time_difference = calculatorValues?.cancellationTime;
    }
    fetchReservations(filters).then((res) => {
      setData(res.data);
      setPageCount(res.pageCount);
      setFetchingDataLoader(false);
    });
  }, [calculatorValues, itemsPerPage, page, ability]);

  React.useEffect(() => {
    const filters = {
      status: calculatorValues?.status,
      days_of_week: calculatorValues?.days,
      minimum_guest_per_table: calculatorValues?.minGuestsPerTable,
      visit_date: {
        gteq: calculatorValues?.vistFromDate,
        lteq: calculatorValues?.vistToDate,
      },
      visit_time: {
        gteq: calculatorValues?.vistFromTime,
        lteq: calculatorValues?.vistToTime,
      },
      shift_id: calculatorValues?.shift_id,
    };
    if (calculatorValues?.status === "Canceled") {
      filters.cancellation_time_difference = calculatorValues?.cancellationTime;
      filters.ppa = calculatorValues?.ppa;
      filters.resale_ppa = calculatorValues?.resalePpa;
    }
    calculateValues(filters).then((res) => {
      setCalculatedValues(res);
    });
  }, [calculatorValues, ability]);

  const updateReservations = async (file) => {
    try {
      await uploadReservations(file);
    } catch (error) {
      console.error("Failed to fetch reservations:", error);
    } finally {
      setLoading(false);
      setFileUploadOpen(false);
    }
  };

  const handleUpload = async (file) => {
    // Simulate a file upload API call
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log("Uploaded file:", file);
        updateReservations(file);
        resolve();
      }, 2000); // Simulate a delay for the upload
    });
  };

  const columns = TableColumns?.map((column) => {
    return {
      key: column,
      label: camelize(column),
    };
  });

  const rows = TableRows(data);

  return (
    <React.Fragment>
      <Box sx={containerStyles}>
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Reservations</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>
        <MenuHeading>Reservations</MenuHeading>
        <Typography
          variant="subtitle1"
          sx={{
            marginBottom: "10px",
            fontSize: "1rem",
            color: "#000",
            fontWeight: 600,
            marginTop: "10px",
          }}
        >
          All the available reservations
        </Typography>

        <ActionContainer>
          <StyledButton
            onClick={() => {
              calculatorOpen
                ? setCalculatorOpen(false)
                : setCalculatorOpen(true);
            }}
          >
            Value Calculator
          </StyledButton>

          <FileUploadButton
            onUpload={handleUpload}
            open={fileUploadOpen}
            setOpen={setFileUploadOpen}
            loading={loading}
            setLoading={setLoading}
            uploadPermission={superAdmin?.role === 'admin'}
          />
        </ActionContainer>
        {calculatorOpen && (
          <Calculator
            calculatedValues={calculatedValues}
            values={calculatorValues}
            handleChangeField={handleChangeField}
          />
        )}

          {fetchingDataLoader ? (
            <Loader />
          ) : (
            <IndexTable
              columns={columns}
              data={rows}
              tableId="reservations"
              type="entries"
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              page={page}
              setPage={setPage}
              pageCount={pageCount}
            />
          )}
      </Box>
    </React.Fragment>
  );
}
